import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { authService, cursosService, tareasService } from '../services/api';
import ConfirmPopup from '../components/ConfirmPopup';
import HtmlContent from '../components/HtmlContent';
import { stripHtml } from '../components/HtmlContent';
import { etiquetaTiposArchivo, categoriasArchivoParaTarea } from '../constants/tareaArchivoTipos';
import { formatDuracionClase } from '../utils/duration';
import './MisCursos.css';
import './Tareas.css';

/** Lenguaje Prism para el highlighter (html -> markup) */
const getPrismLanguage = (lang) => {
  if (!lang || lang === 'plaintext') return 'plaintext';
  return lang === 'html' ? 'markup' : lang;
};

/** Extensión de archivo según lenguaje (para descargar código). */
const getExtensionForLanguage = (lang) => {
  const map = {
    python: 'py', javascript: 'js', typescript: 'ts', html: 'html', markup: 'html',
    css: 'css', json: 'json', sql: 'sql', java: 'java', c: 'c', cpp: 'cpp', php: 'php',
    ruby: 'rb', go: 'go', plaintext: 'txt',
  };
  return map[String(lang || 'plaintext').toLowerCase()] || 'txt';
};

const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

const formatHora = (horaStr) => {
  if (!horaStr) return '-';
  if (typeof horaStr === 'string' && horaStr.length >= 5) {
    return horaStr.substring(0, 5);
  }
  return horaStr;
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('es-AR');
};

/** Solo fecha (evita UTC para no mostrar un día menos). */
const formatDateOnly = (dateString) => {
  if (!dateString) return '-';
  const s = String(dateString).trim().slice(0, 10);
  const parts = s.split('-').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return formatDate(dateString);
  const [y, m, d] = parts;
  return new Date(y, m - 1, d).toLocaleDateString('es-AR');
};

/** Fecha de cierre para mostrar al estudiante: día + hora límite (24 h). Si el API solo envía fecha, es fin del día 23:59. */
const formatFechaCierreConHora = (dateString) => {
  if (!dateString) return '-';
  const s = String(dateString).trim();
  const fecha = formatDateOnly(dateString);
  const hasTime = /[T ]\d{1,2}:\d{2}/.test(s);
  if (!hasTime) {
    return `${fecha} — 23:59 h`;
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) {
    return `${fecha} — 23:59 h`;
  }
  const hora = d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${fecha} — ${hora} h`;
};

/** Parsea string YYYY-MM-DD como fecha local (medianoche) para comparaciones. */
const parseDateOnlyLocal = (dateString) => {
  if (!dateString) return null;
  const s = String(dateString).trim().slice(0, 10);
  const parts = s.split('-').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return new Date(dateString);
  return new Date(parts[0], parts[1] - 1, parts[2]);
};

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  return d.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
};

const TIPOS_ENTREGA_LABELS = { archivo: 'Archivo', enlace: 'Enlace', texto: 'Texto', codigo: 'Código' };
const TIPOS_MATERIAL_LABELS = { archivo: 'Archivo', enlace: 'Enlace', codigo: 'Código', texto: 'Texto', video: 'Video' };

/** Verifica si pasó la fecha de cierre (o nueva_fecha_cierre / habilitación individual). */
const pasoFechaCierre = (tarea, entrega) => {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const base = parseDateOnlyLocal(entrega?.nueva_fecha_cierre || tarea?.fecha_cierre);
  const hab = parseDateOnlyLocal(tarea?.mi_habilitacion_fecha_limite);
  let fechaLimite = base;
  if (hab && (!fechaLimite || hab > fechaLimite)) fechaLimite = hab;
  if (!fechaLimite) return false;
  fechaLimite.setHours(0, 0, 0, 0);
  return hoy > fechaLimite;
};

/** Indica si la entrega fue hecha fuera de la fecha límite (fecha_cierre o nueva_fecha_cierre si aplica). */
const esEntregaFueraDeFecha = (entrega, tarea) => {
  if (!entrega || !entrega.fecha_entrega) return false;
  const raw = entrega?.nueva_fecha_cierre || tarea?.fecha_cierre;
  const fechaCierre = parseDateOnlyLocal(raw);
  if (!fechaCierre) return false;
  const fechaEnt = new Date(entrega.fecha_entrega);
  fechaCierre.setHours(23, 59, 59, 999);
  return fechaEnt > fechaCierre;
};

/** Estado de la tarea para el estudiante: label y clase CSS para el color.
 *  Sin entrega: si no pasó la fecha de cierre → Pendiente; si pasó → No entregado. */
const getEstadoTarea = (entrega, tarea) => {
  if (!entrega) {
    if (!pasoFechaCierre(tarea, null) || tarea?.aceptar_vencidos) {
      return { label: 'Pendiente', className: 'mis-cursos-estado-pendiente' };
    }
    return { label: 'No entregado', className: 'mis-cursos-estado-no-entregado' };
  }
  if (entrega.pedir_reentrega) return { label: 'Reentrega', className: 'mis-cursos-estado-reentrega' };
  if (entrega.revisado) return { label: 'Corregido', className: 'mis-cursos-estado-corregido' };
  return { label: 'Entregado', className: 'mis-cursos-estado-entregado' };
};

/** Obtiene la URL de embed de YouTube a partir de watch o youtu.be */
const getYoutubeEmbedUrl = (url) => {
  const s = String(url || '').trim();
  const match = s.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
};

const MisCursos = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cursos, setCursos] = useState([]);
  const [clases, setClases] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandidoClasesCursoId, setExpandidoClasesCursoId] = useState(null);
  const [expandidoTareasCursoId, setExpandidoTareasCursoId] = useState(null);
  const [confirmBaja, setConfirmBaja] = useState({ show: false, curso: null });
  const [claseModal, setClaseModal] = useState(null);
  const [tareaModal, setTareaModal] = useState(null);
  const [codigoModal, setCodigoModal] = useState(null);
  const [entregasPorTarea, setEntregasPorTarea] = useState({});
  const [descripcionModal, setDescripcionModal] = useState(null);
  const [pestana, setPestana] = useState('en-curso'); // 'en-curso' | 'finalizados'
  const [pestanaFinalizados, setPestanaFinalizados] = useState('aprobados'); // 'aprobados' | 'abandonados'

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    setLoading(true);
    Promise.all([cursosService.getMisCursos(), cursosService.getMisCursosContenido()])
      .then(([cursosData, contenido]) => {
        const list = Array.isArray(cursosData) ? cursosData : [];
        setCursos(list);
        setClases(Array.isArray(contenido?.clases) ? contenido.clases : []);
        setTareas(Array.isArray(contenido?.tareas) ? contenido.tareas : []);
        const entregasRaw = contenido?.entregas && typeof contenido.entregas === 'object' ? contenido.entregas : {};
        const entregasMap = {};
        Object.keys(entregasRaw).forEach((key) => {
          const entrega = entregasRaw[key];
          if (entrega) entregasMap[Number(key) || key] = entrega;
        });
        setEntregasPorTarea(entregasMap);
      })
      .catch(() => {
        setError('Error al cargar tus cursos');
        setCursos([]);
        setClases([]);
        setTareas([]);
      })
      .finally(() => setLoading(false));
  }, [navigate, location.pathname]);

  const clasesDelCurso = (cursoId) =>
    clases.filter((c) => c.curso === cursoId || c.curso === parseInt(cursoId, 10));
  /** Tareas por fecha de cierre descendente (más nueva primero), igual que en /dashboard/tareas */
  const tareasDelCurso = (cursoId) => {
    const raw = tareas.filter((t) => t.curso === cursoId || t.curso === parseInt(cursoId, 10));
    return [...raw].sort((a, b) => {
      const fa = (a.fecha_cierre || '').toString().slice(0, 10);
      const fb = (b.fecha_cierre || '').toString().slice(0, 10);
      if (!fa && !fb) {
        return (b.created_at || '').toString().localeCompare((a.created_at || '').toString());
      }
      if (!fa) return 1;
      if (!fb) return -1;
      if (fb !== fa) return fb.localeCompare(fa);
      return (b.created_at || '').toString().localeCompare((a.created_at || '').toString());
    });
  };

  const toggleClases = (cursoId) => {
    setExpandidoClasesCursoId((prev) => (prev === cursoId ? null : cursoId));
  };
  const toggleTareas = (cursoId) => {
    setExpandidoTareasCursoId((prev) => (prev === cursoId ? null : cursoId));
  };

  const cursosActivos = cursos.filter((c) => c.estado === 'En curso');
  const cursosAprobados = cursos.filter((c) => c.estado === 'Aprobado');
  // Abandonados: dados de baja o finalizados sin aprobar
  const cursosAbandonados = cursos.filter(
    (c) => c.estado === 'Abandonado' || c.estado === 'No aprobado' || c.dado_de_baja === true
  );
  const cursosFinalizadosCount = cursosAprobados.length + cursosAbandonados.length;

  const renderCursoCard = (curso, { esHistorial = false } = {}) => {
    // Backend: acceso solo si el curso no finalizó, o finalizó y el alumno está aprobado
    const mostrarContenido = curso.acceso_contenido === true || (!esHistorial && curso.estado === 'En curso');
    const numClases = clasesDelCurso(curso.id).length;
    const numTareas = tareasDelCurso(curso.id).length;
    const mostrarClases = expandidoClasesCursoId === curso.id;
    const mostrarTareas = expandidoTareasCursoId === curso.id;
    const estadoClass =
      curso.estado === 'Aprobado'
        ? 'mis-cursos-estado-badge-aprobado'
        : curso.estado === 'No aprobado' || curso.estado === 'Abandonado' || curso.dado_de_baja
        ? 'mis-cursos-estado-badge-no-aprobado'
        : 'mis-cursos-estado-badge-en-curso';
    const estadoLabel =
      curso.fecha_baja || curso.dado_de_baja
        ? 'Dado de baja'
        : curso.estado === 'Abandonado'
        ? 'Abandonado'
        : curso.estado === 'No aprobado'
        ? 'Abandonado'
        : curso.estado;

    return (
      <div key={curso.id} className={`mis-cursos-card${esHistorial ? ' mis-cursos-card-historial' : ''}`}>
        <div className="mis-cursos-card-header">
          <h3>{curso.nombre}</h3>
          <div className="mis-cursos-card-badges">
            {(curso.nivel_nombre || (curso.nivel && typeof curso.nivel === 'object' && curso.nivel.nombre)) && (
              <span className="mis-cursos-badge-nivel" aria-label={`Nivel: ${curso.nivel_nombre || curso.nivel?.nombre}`}>
                {curso.nivel_nombre || curso.nivel.nombre}
              </span>
            )}
            {curso.estado && (
              <span className={`mis-cursos-estado-badge ${estadoClass}`}>
                {estadoLabel}
              </span>
            )}
          </div>
        </div>
        {curso.descripcion && (
          (() => {
            const MAX_DESC_PREVIEW = 220;
            const descPlain = stripHtml(curso.descripcion);
            const debeTruncar = descPlain.length > MAX_DESC_PREVIEW;
            return (
              <>
                <HtmlContent
                  content={curso.descripcion}
                  tag="p"
                  className="mis-cursos-desc"
                  maxLength={debeTruncar ? MAX_DESC_PREVIEW : undefined}
                />
                {debeTruncar && (
                  <button
                    type="button"
                    className="mis-cursos-btn-ver-mas-desc"
                    onClick={() => setDescripcionModal(curso)}
                  >
                    Ver más
                  </button>
                )}
              </>
            );
          })()
        )}
        <div className="mis-cursos-info">
          {!esHistorial && (
            <>
              <div className="mis-cursos-info-row">
                <strong>Día(s) de clase:</strong>{' '}
                {curso.dias_semana && curso.dias_semana.length > 0
                  ? curso.dias_semana.map((d) => DIAS_SEMANA[d]).join(', ')
                  : '-'}
              </div>
              <div className="mis-cursos-info-row">
                <strong>Horario:</strong> {formatHora(curso.hora_inicio_clase)}
                {(() => {
                  const d = formatDuracionClase(curso.duracion_clase);
                  return d ? ` (${d})` : '';
                })()}
              </div>
            </>
          )}
          <div className="mis-cursos-info-row">
            <strong>Período:</strong> {formatDateOnly(curso.fecha_inicio)} – {formatDateOnly(curso.fecha_fin)}
          </div>
          {esHistorial && curso.fecha_baja && (
            <div className="mis-cursos-info-row">
              <strong>Fecha de baja:</strong> {formatDateOnly(curso.fecha_baja)}
            </div>
          )}
        </div>

        {esHistorial && !mostrarContenido && (
          <p className="mis-cursos-historial-aviso">
            {curso.dado_de_baja
              ? 'Te diste de baja de este curso. Ya no tenés acceso a clases ni tareas.'
              : 'Curso finalizado. Ya no tenés acceso a clases ni tareas.'}
          </p>
        )}

        {mostrarContenido && (
          <>
            <div className="mis-cursos-seccion">
              <button
                type="button"
                className="mis-cursos-seccion-titulo"
                onClick={() => toggleClases(curso.id)}
                aria-expanded={mostrarClases}
              >
                <span>📚 Clases ({numClases})</span>
                <span className="mis-cursos-seccion-icono">{mostrarClases ? '▼' : '▶'}</span>
              </button>
              {mostrarClases && (
                <div className="mis-cursos-seccion-contenido">
                  {numClases === 0 ? (
                    <p className="mis-cursos-sin-items">No hay clases publicadas para este curso.</p>
                  ) : (
                    <div className="mis-cursos-subcards">
                      {clasesDelCurso(curso.id).map((clase) => (
                        <div key={clase.id} className="mis-cursos-subcard">
                          <button
                            type="button"
                            className="mis-cursos-subcard-titulo mis-cursos-subcard-titulo-click"
                            onClick={() => setClaseModal(clase)}
                            aria-label={`Ver detalle de ${clase.titulo || 'clase'}`}
                          >
                            <span>{clase.titulo || 'Sin título'}</span>
                            <span className="mis-cursos-seccion-icono">▶</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mis-cursos-seccion">
              <button
                type="button"
                className="mis-cursos-seccion-titulo"
                onClick={() => toggleTareas(curso.id)}
                aria-expanded={mostrarTareas}
              >
                <span>📝 Tareas ({numTareas})</span>
                <span className="mis-cursos-seccion-icono">{mostrarTareas ? '▼' : '▶'}</span>
              </button>
              {mostrarTareas && (
                <div className="mis-cursos-seccion-contenido">
                  {numTareas === 0 ? (
                    <p className="mis-cursos-sin-items">No hay tareas para este curso.</p>
                  ) : (
                    <div className="mis-cursos-subcards">
                      {tareasDelCurso(curso.id).map((tarea) => {
                        const estado = getEstadoTarea(entregasPorTarea[tarea.id], tarea);
                        return (
                          <div key={tarea.id} className="mis-cursos-subcard">
                            <button
                              type="button"
                              className="mis-cursos-subcard-titulo mis-cursos-subcard-titulo-click"
                              onClick={() => setTareaModal(tarea)}
                              aria-label={`Ver detalle de ${tarea.titulo || 'tarea'}`}
                            >
                              <span>{tarea.titulo || 'Sin título'}</span>
                              <span className={`mis-cursos-tarea-estado ${estado.className}`}>{estado.label}</span>
                              {esEntregaFueraDeFecha(entregasPorTarea[tarea.id], tarea) && (
                                <span className="mis-cursos-tarea-estado mis-cursos-estado-fuera-de-fecha">Fuera de fecha</span>
                              )}
                              <span className="mis-cursos-seccion-icono">▶</span>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        <div className="mis-cursos-card-actions">
          {!esHistorial && curso.estado === 'En curso' && (
            <button
              type="button"
              className="mis-cursos-btn mis-cursos-btn-baja"
              onClick={() => setConfirmBaja({ show: true, curso })}
              title="Dejar el curso"
              aria-label="Darme de baja"
            >
              Darme de baja
            </button>
          )}
          {!esHistorial && curso.link_clase && (
            <button
              type="button"
              className="mis-cursos-btn mis-cursos-btn-reunion"
              onClick={() => window.open(curso.link_clase, '_blank', 'noopener,noreferrer')}
              title="Abrir link de la clase (reunión virtual)"
            >
              Reunión Virtual
            </button>
          )}
          {(curso.certificado === true || curso.certificado === 'true') && (
            <button
              type="button"
              className="mis-cursos-btn mis-cursos-btn-certificado"
              onClick={async () => {
                try {
                  const blob = await cursosService.getCertificadoPdf(curso.id);
                  const url = URL.createObjectURL(blob);
                  window.open(url, '_blank', 'noopener');
                } catch (err) {
                  console.error(err);
                }
              }}
              title="Ver o descargar certificado"
              aria-label="Ver certificado"
            >
              🏆 Certificado
            </button>
          )}
          {!esHistorial && (
            <button
              type="button"
              className="mis-cursos-btn mis-cursos-btn-asistencia"
              onClick={() => navigate('/dashboard/mi-asistencia')}
            >
              Mi Asistencia
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="mis-cursos-container">
        <div className="mis-cursos-loading">
          <div className="loading-spinner"></div>
          <p>Cargando tus cursos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mis-cursos-container">
      <div className="mis-cursos-header">
        <h1>Mis Cursos</h1>
        <p>Cursos a los que estás inscripto</p>
      </div>

      <ConfirmPopup
        open={Boolean(error)}
        title="Error"
        message={error}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setError('')}
        onCancel={() => setError('')}
        variant="danger"
      />

      {cursos.length === 0 && !error ? (
        <div className="mis-cursos-empty">
          <p>No estás inscripto en ningún curso.</p>
          <p>Desde el Dashboard podrás ver los cursos con inscripción abierta.</p>
        </div>
      ) : (
        <>
          <div className="mis-cursos-tabs" role="tablist" aria-label="Mis cursos">
            <button
              type="button"
              role="tab"
              id="tab-en-curso"
              aria-selected={pestana === 'en-curso'}
              aria-controls="panel-en-curso"
              className={`mis-cursos-tab${pestana === 'en-curso' ? ' mis-cursos-tab-activa' : ''}`}
              onClick={() => setPestana('en-curso')}
            >
              En curso
              <span className="mis-cursos-tab-count">{cursosActivos.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              id="tab-finalizados"
              aria-selected={pestana === 'finalizados'}
              aria-controls="panel-finalizados"
              className={`mis-cursos-tab${pestana === 'finalizados' ? ' mis-cursos-tab-activa' : ''}`}
              onClick={() => setPestana('finalizados')}
            >
              Finalizados
              <span className="mis-cursos-tab-count">{cursosFinalizadosCount}</span>
            </button>
          </div>

          {pestana === 'en-curso' && (
            <div
              id="panel-en-curso"
              role="tabpanel"
              aria-labelledby="tab-en-curso"
              className="mis-cursos-tab-panel"
            >
              {cursosActivos.length === 0 ? (
                <p className="mis-cursos-seccion-vacia">No tenés cursos en curso en este momento.</p>
              ) : (
                <div className="mis-cursos-grid">
                  {cursosActivos.map((curso) => renderCursoCard(curso, { esHistorial: false }))}
                </div>
              )}
            </div>
          )}

          {pestana === 'finalizados' && (
            <div
              id="panel-finalizados"
              role="tabpanel"
              aria-labelledby="tab-finalizados"
              className="mis-cursos-tab-panel"
            >
              <div className="mis-cursos-subtabs" role="tablist" aria-label="Cursos finalizados">
                <button
                  type="button"
                  role="tab"
                  id="tab-aprobados"
                  aria-selected={pestanaFinalizados === 'aprobados'}
                  aria-controls="panel-aprobados"
                  className={`mis-cursos-subtab${pestanaFinalizados === 'aprobados' ? ' mis-cursos-subtab-activa' : ''}`}
                  onClick={() => setPestanaFinalizados('aprobados')}
                >
                  Aprobados
                  <span className="mis-cursos-tab-count">{cursosAprobados.length}</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  id="tab-abandonados"
                  aria-selected={pestanaFinalizados === 'abandonados'}
                  aria-controls="panel-abandonados"
                  className={`mis-cursos-subtab${pestanaFinalizados === 'abandonados' ? ' mis-cursos-subtab-activa' : ''}`}
                  onClick={() => setPestanaFinalizados('abandonados')}
                >
                  Abandonados
                  <span className="mis-cursos-tab-count">{cursosAbandonados.length}</span>
                </button>
              </div>

              {pestanaFinalizados === 'aprobados' && (
                <div id="panel-aprobados" role="tabpanel" aria-labelledby="tab-aprobados">
                  {cursosAprobados.length === 0 ? (
                    <p className="mis-cursos-seccion-vacia">Todavía no tenés cursos aprobados.</p>
                  ) : (
                    <div className="mis-cursos-grid">
                      {cursosAprobados.map((curso) => renderCursoCard(curso, { esHistorial: true }))}
                    </div>
                  )}
                </div>
              )}

              {pestanaFinalizados === 'abandonados' && (
                <div id="panel-abandonados" role="tabpanel" aria-labelledby="tab-abandonados">
                  {cursosAbandonados.length === 0 ? (
                    <p className="mis-cursos-seccion-vacia">No hay cursos abandonados ni dados de baja.</p>
                  ) : (
                    <div className="mis-cursos-grid">
                      {cursosAbandonados.map((curso) => renderCursoCard(curso, { esHistorial: true }))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Modal detalle de clase */}
      {descripcionModal && (
        <div
          className="mis-cursos-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mis-cursos-modal-descripcion-title"
        >
          <div className="mis-cursos-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="mis-cursos-modal-header">
              <h3 id="mis-cursos-modal-descripcion-title">Descripción del curso</h3>
              <button
                type="button"
                className="mis-cursos-modal-close"
                onClick={() => setDescripcionModal(null)}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <div className="mis-cursos-modal-body">
              {descripcionModal.descripcion && (
                <div className="mis-cursos-modal-block">
                  <div className="mis-cursos-modal-desc">
                    <HtmlContent content={descripcionModal.descripcion} tag="div" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {claseModal && (
        <div
          className="mis-cursos-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mis-cursos-modal-clase-title"
        >
          <div className="mis-cursos-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="mis-cursos-modal-header">
              <h3 id="mis-cursos-modal-clase-title">{claseModal.titulo || 'Clase'}</h3>
              <button
                type="button"
                className="mis-cursos-modal-close"
                onClick={() => setClaseModal(null)}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <div className="mis-cursos-modal-body">
              {claseModal.descripcion && (
                <div className="mis-cursos-modal-block">
                  <strong>Descripción:</strong>
                  <div className="mis-cursos-modal-desc"><HtmlContent content={claseModal.descripcion} tag="div" /></div>
                </div>
              )}
              {claseModal.fecha_programada && (
                <p className="mis-cursos-modal-row"><strong>Fecha programada:</strong> {formatDateTime(claseModal.fecha_programada)}</p>
              )}
              {claseModal.fecha_publicacion && (
                <p className="mis-cursos-modal-row"><strong>Fecha publicación:</strong> {formatDateTime(claseModal.fecha_publicacion)}</p>
              )}
              {claseModal.material && claseModal.material.length > 0 && (
                <div className="mis-cursos-modal-block mis-cursos-modal-material">
                  <strong>Material:</strong>
                  {claseModal.material.map((mat, idx) => {
                    const contenido = mat.contenido || '';
                    const tipo = (mat.tipo || '').toLowerCase();
                    const esUrl = /^https?:\/\//i.test(contenido.trim());
                    // Solo mostrar "Descargar" cuando es un archivo real (PDF, etc.). No para video, enlace, código ni texto.
                    const linkDescargar = tipo === 'archivo' && esUrl ? (
                      <a href={contenido.trim()} target="_blank" rel="noopener noreferrer" download className="mis-cursos-material-descargar">
                        <span className="mis-cursos-material-descargar-icono" aria-hidden>⬇</span> Descargar
                      </a>
                    ) : null;

                    if (tipo === 'codigo') {
                      const lang = getPrismLanguage(mat.lenguaje);
                      const lineas = contenido.split('\n');
                      const masDeCinco = lineas.length > 5;
                      const textoMostrar = masDeCinco ? lineas.slice(0, 5).join('\n') : contenido;
                      return (
                        <div key={idx} className="mis-cursos-material-item-wrap">
                          <div className="mis-cursos-material-codigo-wrap mis-cursos-material-codigo-syntax">
                            <SyntaxHighlighter
                              language={lang}
                              style={oneDark}
                              customStyle={{ margin: 0, padding: '1rem', borderRadius: '8px', fontSize: '0.875rem' }}
                              codeTagProps={{ style: { fontFamily: 'Consolas, Monaco, Courier New, monospace' } }}
                              showLineNumbers={lineas.length > 3}
                            >
                              {textoMostrar}
                            </SyntaxHighlighter>
                            {masDeCinco && (
                              <button
                                type="button"
                                className="mis-cursos-btn-ver-mas-codigo"
                                onClick={() => setCodigoModal({ contenido, lenguaje: mat.lenguaje, titulo: 'Código' })}
                              >
                                Ver más
                              </button>
                            )}
                          </div>
                          {linkDescargar}
                        </div>
                      );
                    }
                    if (tipo === 'video') {
                      const embedUrl = getYoutubeEmbedUrl(contenido);
                      if (!embedUrl) {
                        return (
                          <div key={idx} className="mis-cursos-material-item-wrap">
                            <p className="mis-cursos-material-item">
                              <a href={contenido.trim()} target="_blank" rel="noopener noreferrer" className="mis-cursos-material-enlace">
                                Ver video: {contenido.trim()}
                              </a>
                            </p>
                            {linkDescargar}
                          </div>
                        );
                      }
                      return (
                        <div key={idx} className="mis-cursos-material-item-wrap">
                          <div className="mis-cursos-material-video-wrap">
                            <iframe
                              title="Video de YouTube"
                              src={embedUrl}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              className="mis-cursos-material-video"
                            />
                          </div>
                          {linkDescargar}
                        </div>
                      );
                    }
                    if (tipo === 'enlace' && esUrl) {
                      const textoVisible = (mat.titulo || '').trim() || contenido.trim();
                      return (
                        <div key={idx} className="mis-cursos-material-item-wrap">
                          <p className="mis-cursos-material-item">
                            <a
                              href={contenido.trim()}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mis-cursos-material-enlace"
                              title={contenido.trim()}
                            >
                              {textoVisible}
                            </a>
                          </p>
                          {linkDescargar}
                        </div>
                      );
                    }
                    if (tipo === 'archivo') {
                      let nombreArchivo = mat.nombre_original || (contenido || 'Archivo');
                      if (esUrl && !mat.nombre_original) {
                        const desdeUrl = contenido.trim().split('/').pop() || '';
                        const sinPrefijo = /^[a-f0-9]{8,12}_(.+)$/i.exec(desdeUrl);
                        nombreArchivo = sinPrefijo ? sinPrefijo[1] : (desdeUrl || 'Archivo');
                      } else if (!esUrl) {
                        nombreArchivo = contenido || 'Archivo';
                      }
                      return (
                        <div key={idx} className="mis-cursos-material-item-wrap mis-cursos-material-archivo-card-wrap">
                          <div className="mis-cursos-material-archivo-card">
                            <span className="mis-cursos-material-archivo-icono" aria-hidden>📎</span>
                            <span className="mis-cursos-material-archivo-nombre" title={nombreArchivo}>
                              {nombreArchivo}
                            </span>
                            {esUrl ? (
                              <a href={contenido.trim()} target="_blank" rel="noopener noreferrer" download className="mis-cursos-material-archivo-descargar">
                                Descargar
                              </a>
                            ) : null}
                          </div>
                        </div>
                      );
                    }
                    if (tipo === 'texto' && /<[a-z][\s\S]*>/i.test(contenido)) {
                      return (
                        <div key={idx} className="mis-cursos-material-item-wrap">
                          <div className="mis-cursos-material-item mis-cursos-material-texto">
                            <HtmlContent content={contenido} tag="div" />
                          </div>
                          {linkDescargar}
                        </div>
                      );
                    }
                    return (
                      <div key={idx} className="mis-cursos-material-item-wrap">
                        <p className="mis-cursos-material-item mis-cursos-material-texto">
                          {contenido}
                        </p>
                        {linkDescargar}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="mis-cursos-modal-actions">
              <button type="button" className="mis-cursos-btn mis-cursos-btn-asistencia" onClick={() => setClaseModal(null)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal detalle de tarea */}
      {tareaModal && (
        <div
          className="mis-cursos-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mis-cursos-modal-tarea-title"
        >
          <div className="mis-cursos-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="mis-cursos-modal-header">
              <h3 id="mis-cursos-modal-tarea-title">{tareaModal.titulo || 'Tarea'}</h3>
              <button
                type="button"
                className="mis-cursos-modal-close"
                onClick={() => setTareaModal(null)}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <div className="mis-cursos-modal-body">
              {tareaModal.descripcion && (
                <div className="mis-cursos-modal-block">
                  <strong>Descripción:</strong>
                  <div className="mis-cursos-modal-desc"><HtmlContent content={tareaModal.descripcion} tag="div" /></div>
                </div>
              )}
              {tareaModal.archivos_material?.length > 0 && (
                <div className="mis-cursos-modal-block">
                  <strong>Material de la actividad:</strong>
                  <div className="tareas-correccion-archivos-grid mis-cursos-tarea-material-grid">
                    {tareaModal.archivos_material.map((a) => (
                      <div key={a.id} className="tareas-correccion-archivo-card">
                        <span className="tareas-correccion-archivo-icono" aria-hidden>📎</span>
                        <span className="tareas-correccion-archivo-nombre" title={a.nombre_original || 'Archivo'}>
                          {a.nombre_original || 'Archivo'}
                        </span>
                        <button
                          type="button"
                          className="tareas-correccion-archivo-descargar"
                          onClick={() => tareasService.descargarArchivoTarea(a.id, a.nombre_original)}
                        >
                          Descargar
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <p className="mis-cursos-modal-row"><strong>Fecha de cierre:</strong> {formatFechaCierreConHora(tareaModal.fecha_cierre)}</p>
              <p className="mis-cursos-modal-row"><strong>Se califica:</strong> {tareaModal.se_califica ? 'Sí' : 'No'}</p>
              <p className="mis-cursos-modal-row"><strong>Obligatorio:</strong> {tareaModal.es_obligatorio ? 'Sí' : 'No'}</p>
              {tareaModal.tipos_entrega && tareaModal.tipos_entrega.length > 0 && (
                <p className="mis-cursos-modal-row">
                  <strong>Tipos de entrega:</strong>{' '}
                  {tareaModal.tipos_entrega.map((t) => TIPOS_ENTREGA_LABELS[t] || t).join(', ')}
                </p>
              )}
              {tareaModal.tipos_entrega?.includes('archivo') && (
                <p className="mis-cursos-modal-row">
                  <strong>Archivos permitidos:</strong>{' '}
                  {etiquetaTiposArchivo(categoriasArchivoParaTarea(tareaModal), tareaModal?.extensiones_script)}
                </p>
              )}
              {entregasPorTarea[tareaModal.id] && (
                <div className="mis-cursos-modal-block mis-cursos-entregada-badge">
                  {entregasPorTarea[tareaModal.id].pedir_reentrega && entregasPorTarea[tareaModal.id].nueva_fecha_cierre ? (
                    <>
                      <div className="mis-cursos-reentrega-aviso">
                        <strong>Debe reentregar esta tarea</strong>
                        <p className="mis-cursos-modal-row">
                          Nueva fecha de cierre: <strong>{formatFechaCierreConHora(entregasPorTarea[tareaModal.id].nueva_fecha_cierre)}</strong>
                        </p>
                      </div>
                      <p className="mis-cursos-modal-row mis-cursos-entregada-anterior">
                        Entrega anterior: {formatDateTime(entregasPorTarea[tareaModal.id].fecha_entrega)}
                      </p>
                    </>
                  ) : (
                    <>
                      <strong>✓ Entregada</strong>
                      {esEntregaFueraDeFecha(entregasPorTarea[tareaModal.id], tareaModal) && (
                        <span className="mis-cursos-tarea-estado mis-cursos-estado-fuera-de-fecha" style={{ marginLeft: '0.5rem' }}>Fuera de fecha</span>
                      )}
                      <p className="mis-cursos-modal-row">
                        Fecha de entrega: {formatDateTime(entregasPorTarea[tareaModal.id].fecha_entrega)}
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="mis-cursos-modal-actions">
              {(() => {
                const entregaTarea = entregasPorTarea[tareaModal.id];
                const tareaCorregida = Boolean(entregaTarea?.revisado && !entregaTarea?.pedir_reentrega);
                return (
                  <button
                    type="button"
                    className="mis-cursos-btn mis-cursos-btn-estado-tarea"
                    onClick={() => {
                      setTareaModal(null);
                      if (tareaCorregida && entregaTarea?.id) {
                        navigate(`/dashboard/ver-correccion/${entregaTarea.id}`, {
                          state: { from: 'calificaciones' },
                        });
                      } else {
                        navigate('/dashboard/calificaciones');
                      }
                    }}
                  >
                    {tareaCorregida ? 'Ver Corrección' : 'Ver en Correcciones'}
                  </button>
                );
              })()}
              <button
                type="button"
                className="mis-cursos-btn mis-cursos-btn-entregar"
                onClick={() => navigate(`/dashboard/entregar/${tareaModal.id}`)}
                disabled={(() => {
                  const entrega = entregasPorTarea[tareaModal.id];
                  const vencida = pasoFechaCierre(tareaModal, entrega) && !tareaModal.aceptar_vencidos;
                  if (!entrega) return vencida;
                  if (entrega.pedir_reentrega) return vencida;
                  if (entrega.revisado) return true;
                  return vencida;
                })()}
              >
                {(() => {
                  const entrega = entregasPorTarea[tareaModal.id];
                  if (!entrega) return 'Entregar tarea';
                  if (entrega.pedir_reentrega) return 'Reentregar tarea';
                  if (entrega.revisado) return 'Ya entregada';
                  return 'Modificar entrega';
                })()}
              </button>
              <button type="button" className="mis-cursos-btn mis-cursos-btn-asistencia" onClick={() => setTareaModal(null)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal ver código completo (encima de clase/tarea) */}
      {codigoModal && (
        <div className="mis-cursos-modal-overlay mis-cursos-modal-codigo-overlay" role="dialog" aria-modal="true" aria-labelledby="mis-cursos-codigo-modal-title">
          <div className="mis-cursos-modal-box mis-cursos-modal-codigo-box" onClick={(e) => e.stopPropagation()}>
            <div className="mis-cursos-modal-header">
              <h3 id="mis-cursos-codigo-modal-title">{codigoModal.titulo || 'Código'}</h3>
              <button type="button" className="mis-cursos-modal-close" onClick={() => setCodigoModal(null)} aria-label="Cerrar">×</button>
            </div>
            <div className="mis-cursos-modal-body mis-cursos-modal-codigo-body">
              <div className="mis-cursos-material-codigo-wrap mis-cursos-material-codigo-syntax">
                <SyntaxHighlighter
                  language={getPrismLanguage(codigoModal.lenguaje)}
                  style={oneDark}
                  customStyle={{ margin: 0, padding: '1rem', borderRadius: '8px', fontSize: '0.875rem', maxHeight: '60vh', overflow: 'auto' }}
                  codeTagProps={{ style: { fontFamily: 'Consolas, Monaco, Courier New, monospace' } }}
                  showLineNumbers={(codigoModal.contenido || '').split('\n').length > 3}
                >
                  {codigoModal.contenido || ''}
                </SyntaxHighlighter>
              </div>
            </div>
            <div className="mis-cursos-modal-actions">
              <button
                type="button"
                className="mis-cursos-btn mis-cursos-btn-descargar-codigo"
                onClick={() => {
                  const ext = getExtensionForLanguage(codigoModal.lenguaje);
                  const blob = new Blob([codigoModal.contenido || ''], { type: 'text/plain;charset=utf-8' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `codigo.${ext}`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                ⬇ Descargar
              </button>
              <button type="button" className="mis-cursos-btn mis-cursos-btn-asistencia" onClick={() => setCodigoModal(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmPopup
        open={confirmBaja.show}
        title="Darme de baja del curso"
        message={confirmBaja.curso ? `¿Estás seguro de que querés darte de baja de "${confirmBaja.curso.nombre || 'este curso'}"? Dejarás de estar inscripto.` : ''}
        confirmLabel="Sí, darme de baja"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={async () => {
          if (!confirmBaja.curso) return;
          try {
            await cursosService.darseDeBajaCurso(confirmBaja.curso.id);
            setConfirmBaja({ show: false, curso: null });
            const [data, contenido] = await Promise.all([
              cursosService.getMisCursos(),
              cursosService.getMisCursosContenido(),
            ]);
            const list = Array.isArray(data) ? data : [];
            setCursos(list);
            setClases(Array.isArray(contenido?.clases) ? contenido.clases : []);
            setTareas(Array.isArray(contenido?.tareas) ? contenido.tareas : []);
            const entregasRaw = contenido?.entregas && typeof contenido.entregas === 'object' ? contenido.entregas : {};
            const entregasMap = {};
            Object.keys(entregasRaw).forEach((key) => {
              if (entregasRaw[key]) entregasMap[Number(key) || key] = entregasRaw[key];
            });
            setEntregasPorTarea(entregasMap);
          } catch (err) {
            setError(err.response?.data?.error || err.message || 'Error al darte de baja');
            setConfirmBaja({ show: false, curso: null });
          }
        }}
        onCancel={() => setConfirmBaja({ show: false, curso: null })}
      />
    </div>
  );
};

export default MisCursos;
